import {
  KeyValue,
  Unboxed,
  SetValueAction,
  SetAsyncErrorAction,
  ClearAsyncErrorAction,
  StartAsyncValidationAction,
  unbox,
  AbstractControlState
} from 'ngrx-forms';
import {
  OnInit,
  OnDestroy,
  Injector,
  Injectable
} from '@angular/core';
import {
  Observable,
  Subscription,
  of,
  merge,
  EMPTY,
  Subject
} from 'rxjs';
import {
  Store,
  Action
} from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import {
  IDxForm,
  FormControlLike
} from './form';
import { deepEqual } from '@digitaix/helpers';
import { FormRegisterBuilder } from './form-register-builder';
import { map } from 'rxjs/operators';
import { v1 as uuid } from 'uuid';

import { ValidatorResult } from './decorators/validator';
import {
  DxFormAsyncValidatorDefinition,
  DxFormGroupDefinition,
  DxFormOnSetValueDefinition
} from './decorators/interfaces';
import {
  groupByControlPath,
  validationResultRoValidationError
} from './helpers';
import { StepControl } from './step-control';

@Injectable()
export abstract class AbstractForm<
  FormValue extends KeyValue,
  GroupFormValue extends KeyValue = null,
  RootGroupFormValue extends KeyValue = GroupFormValue,
  Value extends Unboxed<FormValue> = Unboxed<FormValue>
>
  implements
    IDxForm<FormValue, GroupFormValue, RootGroupFormValue>,
    OnInit,
    OnDestroy {
  public get __initialized(): boolean {
    return this._initialized;
  }

  /**
   * @internal
   */
  public set __initialized(value: boolean) {
    this._initialized = value;
    this.forEachControl((_, control) => (control.__initialized = value));
  }

  public get __formId(): string {
    return this.__controlId.match(/^[^\.]+/)[0];
  }

  public get __localControlId(): string | null {
    const match = this.__controlId.match(/^[^\.]+\.(.+)/);
    return match ? match[1] : null;
  }

  public get __stepControl(): FormControlLike<FormValue> {
    return new StepControl(this.store$, this.__state$);
  }

  public __controlId: string;

  public __controlPath: string;

  public __value$: Observable<Value | null>;

  public __value: Value | null = null;

  public __initial: FormValue | null = null;

  public __controlName: string;

  public __parent: AbstractForm<
    GroupFormValue,
    any,
    RootGroupFormValue
  > | null = null;

  public __root: AbstractForm<RootGroupFormValue, null, null> | null = null;

  private _initialized = false;

  protected _subscription = new Subscription();

  public __state$: Observable<
    AbstractControlState<FormValue> | AbstractControlState<FormValue>
  >;

  public __state:
    | AbstractControlState<FormValue>
    | AbstractControlState<FormValue>;

  protected _groupedAsyncValidators!: {
    [controlId: string]: DxFormAsyncValidatorDefinition<any, any>[];
  };

  protected _groupedOnSetValue!: {
    [controlId: string]: DxFormOnSetValueDefinition[];
  };

  public __uuid = uuid();

  public __onInit$ = new Subject<void>();
  public __onDestroy$ = new Subject<void>();
  public __onReset$ = new Subject<void>();

  constructor(
    public readonly store$: Store<any>,
    public readonly logger: NGXLogger,
    public readonly injector: Injector
  ) {
    this.logger.trace(
      '[AbstractForm::setControlId] create instance',
      Object.getPrototypeOf(this).constructor.name
    );
  }

  /**
   * @internal
   */
  public setControlId(controlId: string, parentId?: string): void {
    this.logger.trace(
      `[${this.__uuid}::AbstractForm::setControlId::${this.__controlId}] call`,
      {
        controlId,
        parentId
      }
    );
    this.__controlName = controlId;
    this.__controlId = [parentId, controlId].filter(Boolean).join('.');
    this.__controlPath = toControlPath(this.__controlId);
    this.forEachControl((_controlId, control) =>
      control.setControlId(_controlId, this.__controlId)
    );
    this.logger.trace(
      `[${this.__uuid}::AbstractForm::setControlId::${
        this.__controlId
      }] new controlId`,
      { controlPath: this.__controlPath, controlId, parentId }
    );
  }

  public ngOnDestroy(): void {
    this.logger.trace(
      `[${this.__uuid}::AbstractForm::ngOnDestroy::${this.__controlId}] call`
    );
    this._subscription.unsubscribe();
    this.__onDestroy$.next();
    this._subscription = new Subscription();
    this.forEachControl((_, control) => control.ngOnDestroy());
    this.__value$ = EMPTY;
    this.__value = null;
    this.__onDestroy$.next();
  }

  public abstract forEachControl(
    callback: (controlId: string, control: IDxForm<any, any>) => any
  ): void;

  setValue(value: any): void {
    this.logger.trace(
      `[${this.__uuid}::AbstractForm::setValue::${this.__controlId}] call`,
      value
    );
  }

  public isEqual(a: Value, b: Value): boolean {
    return deepEqual(a, b);
  }

  /**
   * @internal
   */
  public init(): void {
  }

  public ngOnInit(): void {
    this._subscription.add(this.__onReset$.subscribe(() => this.forEachControl((_, control) => control.__onReset$.next())))
  }

  public box(value: Value): FormValue {
    this.logger.trace(
      `[${this.__uuid}::AbstractForm::box::${this.__controlId}] call`,
      value
    );
    return value as any;
  }

  public buildFormGroup<V>(
    definition: DxFormGroupDefinition<V>,
    name?: string
  ): any {
    this.logger.trace(
      `[${this.__uuid}::AbstractForm::buildFormGroup::${this.__controlId}::${
        definition.name
      }] call`,
      definition
    );

    const group = new definition.group(this.store$, this.logger, this.injector);

    group.setControlId(name || definition.name, this.__controlId);
    group.__parent = this;
    // TODO : fix types
    group.__root = this.__root || (this as any);

    return group;
  }

  /**
   * @internal
   */
  public abstract processAsyncValidators(
    action: SetValueAction<any>
  ): Observable<
    SetAsyncErrorAction | ClearAsyncErrorAction | StartAsyncValidationAction
  >;

  public abstract processOnSetValue(
    action: SetValueAction<any>
  ): Observable<void | Action>;

  public processAsyncValidatorsDecorator(builder: FormRegisterBuilder): void {
    this._groupedAsyncValidators = groupByControlPath(
      builder.asyncValidators.map(av => {
        const defaultValidatorErrorTemplate = [
          'FORMS',
          this.__formId,
          'VALIDATION',
          this.__localControlId,
          'ERRORS',
          av.propertyKey
        ]
          .filter(Boolean)
          .join('.');
        return {
          ...av,
          validatorErrorTemplate:
            av.validatorErrorTemplate || defaultValidatorErrorTemplate
        };
      })
    );
  }

  public processOnSetValueDecorator(builder: FormRegisterBuilder): void {
    this._groupedOnSetValue = groupByControlPath(builder.onSetValues);
  }

  public abstract getControlByControlId(controlId: string): IDxForm<any, any>;

  public getControl<V, D extends IDxForm<V, any, any>>(controlPath: string): D {
    const normelizedControlPath = controlPath.replace(/^\.\//, '');
    const normelizedFragemnts = normelizedControlPath.split('/');
    const currentFragment = normelizedFragemnts.shift();
    this.logger.trace(
      `[${this.__uuid}::AbstractForm::getControl::${this.__controlId}] call`,
      {
        controlPath,
        normelizedControlPath,
        normelizedFragemnts,
        currentFragment
      }
    );

    if (currentFragment === '..') {
      if (!this.__parent) {
        throw new Error(
          `[${this.__uuid}::AbstractForm::getControl::${
            this.__controlId
          }] provided path '${controlPath}' is invalid. Parent is undefined`
        );
      }
      return this.__parent.getControl<V, D>(normelizedFragemnts.join('/'));
    } else {
      const control = this.getControlByControlId(currentFragment);
      if (!control) {
        throw new Error(
          `[${this.__uuid}::AbstractForm::getControl::${
            this.__controlId
          }] provided path '${controlPath}' is invalid. Child is undefined`
        );
      }

      if (normelizedFragemnts.length) {
        if (typeof control['getControl'] === 'function') {
          return control['getControl']<V, D>(normelizedFragemnts.join('/'));
        } else {
          throw new Error(
            `[${this.__uuid}::AbstractForm::getControl::${
              this.__controlId
            }] provided path '${controlPath}' is invalid. Reached a Leaf`
          );
        }
      } else {
        return control as any;
      }
    }
  }

  public getValue<V>(controlPath: string): V {
    const normelizedControlPath = controlPath.replace(/^\.\//, '');
    const normelizedFragemnts = normelizedControlPath.split('/');
    const currentFragment = normelizedFragemnts.shift();
    this.logger.trace(
      `[${this.__uuid}::AbstractForm::getValue::${this.__controlId}] call`,
      {
        controlPath,
        normelizedControlPath,
        normelizedFragemnts,
        currentFragment
      }
    );

    if (currentFragment === '..') {
      if (!this.__parent) {
        throw new Error(
          `[${this.__uuid}::AbstractForm::getValue::${
            this.__controlId
          }] provided path '${controlPath}' is invalid. Parent is undefined`
        );
      }
      return this.__parent.getValue<V>(normelizedFragemnts.join('/'));
    } else {
      const control = this.getControlByControlId(currentFragment);
      if (!control) {
        throw new Error(
          `[${this.__uuid}::AbstractForm::getValue::${
            this.__controlId
          }] provided path '${controlPath}' is invalid. Child is undefined`
        );
      }

      if (normelizedFragemnts.length) {
        if (typeof control['getValue'] === 'function') {
          return control['getValue']<V>(normelizedFragemnts.join('/'));
        } else {
          throw new Error(
            `[${this.__uuid}::AbstractForm::getValue::${
              this.__controlId
            }] provided path '${controlPath}' is invalid. Reached a Leaf`
          );
        }
      } else {
        return control.__value;
      }
    }
  }

  public getValue$<V>(controlPath: string): Observable<V> {
    const normelizedControlPath = controlPath.replace(/^\.\//, '');
    const normelizedFragemnts = normelizedControlPath.split('/');
    const currentFragment = normelizedFragemnts.shift();
    this.logger.trace(
      `[${this.__uuid}::AbstractForm::getValue::${this.__controlId}] call`,
      {
        controlPath,
        normelizedControlPath,
        normelizedFragemnts,
        currentFragment
      }
    );

    if (currentFragment === '..') {
      if (!this.__parent) {
        throw new Error(
          `[${this.__uuid}::AbstractForm::getValue::${
            this.__controlId
          }] provided path '${controlPath}' is invalid. Parent is undefined`
        );
      }
      return this.__parent.getValue$<V>(normelizedFragemnts.join('/'));
    } else {
      const control = this.getControlByControlId(currentFragment);
      if (!control) {
        throw new Error(
          `[${this.__uuid}::AbstractForm::getValue::${
            this.__controlId
          }] provided path '${controlPath}' is invalid. Child is undefined`
        );
      }

      if (normelizedFragemnts.length) {
        if (typeof control['getValue$'] === 'function') {
          return control['getValue$']<V>(normelizedFragemnts.join('/'));
        } else {
          throw new Error(
            `[${this.__uuid}::AbstractForm::getValue::${
              this.__controlId
            }] provided path '${controlPath}' is invalid. Reached a Leaf`
          );
        }
      } else {
        return control.__value$;
      }
    }
  }
}

export function normelizeControlPath(
  controlPath: string,
  currentControlPath: string
): string {
  // remove path prefix
  let normelizedControlPath = controlPath.replace(/^\.\//, '');

  let groupControlPath = currentControlPath;

  if (controlPath[0] === '.') {
    const fragments = controlPath.split('/');
    const groupFragemnts = groupControlPath.split('/');

    for (const back of fragments.filter(f => f === '..')) {
      groupFragemnts.pop();
    }

    groupControlPath = groupFragemnts.join('/');

    normelizedControlPath = [
      groupControlPath,
      fragments.filter(f => f !== '..').join('/')
    ].join('/');
  }

  return normelizedControlPath;
}

export function toControlPath(controlId: string): string {
  return controlId.split('.').join('/');
}

export function toControlId(
  controlPath: string,
  parentControlPath: string
): string {
  // remove path prefix
  controlPath = controlPath.replace(/^\.\//, '');

  let groupControlPath = parentControlPath;

  if (controlPath[0] === '.') {
    const fragments = controlPath.split('/');
    const groupFragemnts = groupControlPath.split('/');

    for (const back of fragments.filter(f => f === '..')) {
      groupFragemnts.pop();
    }

    groupControlPath = groupFragemnts.join('/');

    controlPath = fragments.filter(f => f !== '..').join('/');
  }

  return [groupControlPath, controlPath].filter(Boolean).join('/');
}

export function matchRelativeControlPath(
  controlPath: string,
  targetPath: string,
  basePath: string
) {
  // remove path prefix
  controlPath = controlPath.replace(/^\.\//, '');

  if (controlPath[0] === '.') {
    const fragments = controlPath.split('/');
    const groupFragemnts = basePath.split('/');

    for (const back of fragments.filter(f => f === '..')) {
      groupFragemnts.pop();
    }

    basePath = groupFragemnts.join('/');

    controlPath = fragments.filter(f => f !== '..').join('/');
  }

  targetPath = targetPath.replace(new RegExp(`^${basePath}\/`), '');

  return targetPath === controlPath;
}
